import { ByteType, FloatType, HalfFloatType, IntType, ShortType, Texture, UnsignedByteType, UnsignedInt101111Type, UnsignedInt248Type, UnsignedInt5999Type, UnsignedIntType, UnsignedShort4444Type, UnsignedShort5551Type, UnsignedShortType } from "three";
import * as THREE from "three";
import { isCompressedTexture } from "./helper.js";

export function isDepthTexture(texture: unknown): boolean {
    const t = texture as { isDepthTexture?: boolean; isTexture?: boolean; format?: number } | null | undefined;
    return t?.isDepthTexture === true || (t?.isTexture === true && (t.format === THREE.DepthFormat || t.format === THREE.DepthStencilFormat));
}

/**
 * A meaningful class name for a texture, derived from three's `isXxx` duck-typing
 * flags — these survive minification, unlike `constructor.name` (which is `"e"`
 * on a production build). `texture.type` is NOT the class here (it's the data
 * type, e.g. HalfFloatType), so the resource registry can't use it for textures.
 */
export function textureTypeName(texture: object): string {
    const t = texture as Record<string, unknown>;
    if (t.isDepthTexture) return "DepthTexture";
    if (t.isDataArrayTexture) return "DataArrayTexture";
    if (t.isData3DTexture) return "Data3DTexture";
    if (t.isDataTexture) return "DataTexture";
    if (t.isCompressedArrayTexture) return "CompressedArrayTexture";
    if (t.isCompressedTexture) return "CompressedTexture";
    if (t.isCubeTexture) return "CubeTexture";
    if (t.isCanvasTexture) return "CanvasTexture";
    if (t.isVideoTexture) return "VideoTexture";
    if (t.isFramebufferTexture) return "FramebufferTexture";
    // a plain Texture flagged as a render target's attachment (isRenderTargetTexture)
    if (t.isRenderTargetTexture) return "RenderTargetTexture";
    return "Texture";
}

// #region Texture Size

export interface TextureMemoryEstimate {
    /** CPU memory usage in bytes. 0 if texture data is not retained on CPU. */
    cpu: number;
    /** GPU VRAM usage in bytes. */
    vram: number;
}

/**
 * Estimates the memory usage of a texture on both CPU and GPU (VRAM).
 *
 * For compressed textures, calculates the size based on block compression.
 * For uncompressed textures, calculates based on pixel format, data type, and channel count.
 * Includes mipmaps in the estimation when present or when generateMipmaps is enabled.
 *
 * CPU memory is only counted if the texture actually retains the image data on the CPU side.
 * This is typically the case for:
 * - Compressed textures (mipmaps stored in CPU memory)
 * - Textures that haven't been uploaded yet
 * - DataTextures with explicit data arrays
 *
 * @param texture The Three.js texture to estimate memory for
 * @returns An object containing CPU and VRAM memory estimates in bytes
 */
/**
 * True only when the texture holds pixels in a CPU typed array (DataTexture-style)
 * — genuine JS-heap memory. Drawable image sources (ImageBitmap / HTMLImageElement
 * / canvas / video, i.e. every glTF texture) decode to GPU/browser memory and keep
 * no CPU copy after upload, so they must NOT be counted as CPU bytes.
 */
function textureHasCpuPixelBuffer(texture: Texture): boolean {
    const isPixelView = (v: unknown): boolean => ArrayBuffer.isView(v) && !(v instanceof DataView);
    const image = (texture as { image?: { data?: unknown } }).image;
    if (image && isPixelView(image.data)) return true;
    return isPixelView((texture as { data?: unknown }).data);
}

export function estimateTextureMemory(texture: Texture): TextureMemoryEstimate {
    let cpu = 0;
    let vram = 0;

    // CPU memory is only real when the texture retains an actual CPU-side pixel
    // BUFFER: a DataTexture's typed array, or a compressed texture's mipmap typed
    // arrays. Drawable sources — ImageBitmap / HTMLImageElement / canvas / video,
    // which is exactly what glTF textures decode to — live in GPU/browser memory,
    // NOT the JS heap; once uploaded there is no CPU copy. Counting a full RGBA
    // there just DOUBLE-COUNTS the GPU size (a 244 MB VRAM model then reads 244 MB
    // CPU too, larger than the whole JS heap). Only a real typed array counts.
    // Uncompressed textures only: compressed ones are decided PER MIP below, from
    // the bytes each one actually retains.
    const hasDataOnCPU = textureHasCpuPixelBuffer(texture);

    const width = texture.image?.width;
    const height = texture.image?.height;

    if (isCompressedTexture(texture)) {
        // These objects come from the HOST PAGE in the extension — another realm,
        // another three version — so a malformed mip is an expected input. The
        // arithmetic is what makes that dangerous: an undefined width gives NaN,
        // a non-numeric byteLength turns `cpu` into a STRING, and either poisons
        // every total on the panel. A level we cannot size is worth zero.
        const mipmaps = Array.isArray(texture.mipmaps) ? texture.mipmaps : [];
        for (const mip of mipmaps) {
            if (!mip) continue;
            // The GPU holds the SAME block bytes the CPU copy does, so while the
            // buffer is here there is nothing to estimate — measure it.
            //
            // The block math below assumes 4x4 blocks. That is right for BC1/BC3/
            // BC7/ETC2 and WRONG for ASTC, whose 16-byte blocks cover a varying
            // footprint: at 8x8 the real size is a quarter of what 4x4 blocks
            // predict, and the old inference (byteLength / blocks = 4) was
            // rejected for not being 8 or 16, so it fell back to 16 and reported
            // FOUR TIMES the truth.
            //
            // CPU counts only what a mip STILL HOLDS: that buffer can legitimately
            // be gone — detached by a worker transfer, or dropped after upload.
            const bytes = mip.data?.byteLength;
            const retained = typeof bytes === "number" && Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
            cpu += retained;
            if (retained > 0) {
                vram += retained;
                continue;
            }
            // Nothing left to measure — the texture was uploaded and released, so
            // it still occupies VRAM. Estimate at 4x4 blocks of 16 bytes (BC3
            // shaped); an ASTC texture in this state is over-reported, and there
            // is nothing left on our side to tell us otherwise.
            if (!Number.isFinite(mip.width) || !Number.isFinite(mip.height)) continue;
            const numBlocksWide = Math.max(1, Math.floor((mip.width + 3) / 4));
            const numBlocksHigh = Math.max(1, Math.floor((mip.height + 3) / 4));
            vram += numBlocksWide * numBlocksHigh * 16;
        }
    } else {
        // Uncompressed texture memory size estimation

        // Get bytes per pixel based on texture type and format
        let bytesPerPixel = 0;

        // Determine channel count based on texture.format
        let channels = 4; // Default RGBA
        if (texture.format !== undefined) {
            // Common Three.js texture formats
            const format = texture.format;
            // RedFormat = 1 channel, RGFormat = 2, RGBFormat = 3, RGBAFormat = 4
            // DepthFormat = 1, DepthStencilFormat = 2, AlphaFormat = 1, LuminanceFormat = 1, LuminanceAlphaFormat = 2
            // Format values: Alpha=1023, Red=1028, RG=1030, RGB=1029, RGBA=1023
            if (format === 1028) channels = 1; // RedFormat
            else if (format === 1030) channels = 2; // RGFormat
            else if (format === 1029) channels = 3; // RGBFormat
            else if (format === 1023) channels = 4; // RGBAFormat or AlphaFormat
            // For other formats, keep default of 4
        }

        switch (texture.type) {
            case UnsignedByteType:
            case ByteType:
                bytesPerPixel = 1 * channels;
                break;
            case ShortType:
            case UnsignedShortType:
            case HalfFloatType:
                bytesPerPixel = 2 * channels;
                break;
            case IntType:
            case UnsignedIntType:
            case FloatType:
                bytesPerPixel = 4 * channels;
                break;
            // Packed formats - these pack all channels into a single value
            case UnsignedShort4444Type: // RGBA packed into 16 bits (4+4+4+4)
            case UnsignedShort5551Type: // RGBA packed into 16 bits (5+5+5+1)
                bytesPerPixel = 2; // Total 2 bytes for all channels
                break;
            case UnsignedInt248Type: // Depth24 + Stencil8 packed into 32 bits
            case UnsignedInt5999Type: // RGB9_E5 packed into 32 bits (shared exponent)
            case UnsignedInt101111Type: // RG11B10F packed into 32 bits
                bytesPerPixel = 4; // Total 4 bytes for all channels
                break;
            default:
                bytesPerPixel = 4 * channels; // Default to 4 bytes per channel
                break;
        }

        if (width && height) {
            // Calculate size for the main texture level
            let totalSize = width * height * bytesPerPixel;

            // Account for mipmaps if they exist
            // Mipmaps add approximately 1/3 more memory (1 + 1/4 + 1/16 + 1/64 + ... ≈ 1.333)
            if (texture.mipmaps && texture.mipmaps.length > 0) {
                // If mipmaps are explicitly provided, calculate their exact size
                for (const mip of texture.mipmaps) {
                    if (mip.width && mip.height) {
                        totalSize += mip.width * mip.height * bytesPerPixel;
                    }
                }
            } else if (texture.generateMipmaps) {
                // If mipmaps will be auto-generated, estimate their size
                totalSize = Math.floor(totalSize * 1.333);
            }

            vram = totalSize;
            // Only count CPU memory if texture data is actually retained
            if (hasDataOnCPU) {
                cpu = totalSize;
            }
        }
    }

    return { cpu, vram };
}

/**
 * Assumed GPU compression ratio for an UNCOMPRESSED colour texture: RGBA8 is 32bpp,
 * a block-compressed format (BC7 / ASTC-4×4) is ~8bpp ≈ 4×. A heuristic — the saving
 * is an estimate, not exact.
 */
export const GPU_COMPRESSION_RATIO = 4;

/**
 * Estimated VRAM a texture could save by GPU-compressing it (KTX2 / basis). Zero
 * when it is already compressed, is a RENDER TARGET (cannot be block-compressed),
 * or is GENERATED in code — a DataTexture has no source file to re-encode, so the
 * advice would be unactionable, it usually holds non-colour DATA a lossy block
 * format would corrupt, and its type (UnsignedShort, Int, …) is often one basis
 * cannot hold at all. Pure. `ratio` overridable for a more/less conservative
 * assumption.
 */
export function estimateTextureCompressionSaving(
    vram: number,
    opts: { compressed: boolean; renderTarget: boolean; generated?: boolean },
    ratio = GPU_COMPRESSION_RATIO,
): number {
    if (opts.compressed || opts.renderTarget || opts.generated || !(vram > 0) || ratio <= 1) return 0;
    return Math.round(vram * (1 - 1 / ratio));
}

/**
 * Built in code rather than loaded from a file — SMAA's lookup tables, troika's
 * text atlases, gradient ramps. Nothing about them is the user's to re-export.
 */
export function isGeneratedTexture(texture: unknown): boolean {
    return isComputedTexture(texture);
}

/**
 * Was this DataTexture COMPUTED, or loaded from a file?
 *
 * It matters because a computed texture (an IBL lookup table, a procedural ramp)
 * has no file to re-encode, so telling anyone to compress it is noise. But EXR and
 * HDR files also arrive as DataTextures — that is exactly what EXRLoader and
 * RGBELoader produce — so excluding every DataTexture silently dropped the single
 * heaviest thing in most scenes: the uncompressed HDR environment. It counted
 * neither toward the reclaimable VRAM nor against the score.
 *
 * The distinguishing evidence, in order:
 *  - an environment MAPPING (equirect/cube) is never a computed LUT;
 *  - float data at environment RESOLUTION (>= 256 on a side) is an HDR image, not
 *    a lookup table, which are small by nature.
 */
export function isComputedTexture(texture: unknown): boolean {
    if (isDepthTexture(texture)) return true;
    const t = texture as {
        isDataTexture?: boolean;
        type?: unknown;
        mapping?: unknown;
        image?: { width?: unknown; height?: unknown };
    } | null;
    if (t?.isDataTexture !== true) return false;

    // EquirectangularReflection/Refraction and Cube* mappings (three's enum values
    // 301-307) — an environment, whatever produced it
    const mapping = typeof t.mapping === "number" ? t.mapping : 0;
    if (mapping >= 301 && mapping <= 307) return false;

    if (!isHdrTexture(t)) return true; // an LDR DataTexture is a computed thing
    const width = typeof t.image?.width === "number" ? t.image.width : 0;
    const height = typeof t.image?.height === "number" ? t.image.height : 0;
    return Math.max(width, height) < ENVIRONMENT_MIN_SIZE;
}

/** Below this, float data is a lookup table rather than an environment image. */
const ENVIRONMENT_MIN_SIZE = 256;

/**
 * What the texture was BEFORE the GPU got it — "webp", "png", "ktx2" — or
 * undefined when nothing records it.
 *
 * Needed because the preview badge said "Uncompressed" for a webp, which is
 * false about the file and true only about the GPU copy. Naming the source makes
 * the real point: a webp is small to download and still costs
 * width x height x 4 bytes of VRAM.
 */
export function textureSourceFormat(texture: { userData?: { mimeType?: unknown }, image?: { src?: unknown } } | null | undefined): string | undefined {
    const mime = texture?.userData?.mimeType;
    if (typeof mime === "string" && mime.includes("/")) {
        const subtype = mime.split("/")[1]?.toLowerCase().trim();
        if (subtype) return subtype === "jpeg" ? "jpg" : subtype;
    }
    // blob:/data: urls carry no extension — better nothing than a wrong guess
    const src = texture?.image?.src;
    if (typeof src === "string") {
        const path = src.split(/[?#]/)[0];
        const ext = path.includes(".") ? path.slice(path.lastIndexOf(".") + 1).toLowerCase() : "";
        if (ext && ext.length <= 5 && /^[a-z0-9]+$/.test(ext)) return ext === "jpeg" ? "jpg" : ext;
    }
    return undefined;
}

// #region HDR environments (FastHDR)

/** three's float data types. Compared by VALUE so this stays a pure module. */
const HALF_FLOAT_TYPE = 1016;
const FLOAT_TYPE = 1015;

/**
 * VRAM ratio of an EXR/HDR environment against FastHDR (KTX2 UASTC HDR 4x4).
 * From Needle's published measurements: 2K 64 MB -> 3 MB, 4K 256 MB -> 12 MB
 * (documentation/explanation/fasthdr.md). ~21x, taken conservatively.
 */
export const FASTHDR_VRAM_RATIO = 21;

/** File-size ratio of the same conversion: 2K 5.6 -> 1.9 MB, 4K 21.2 -> 7.3 MB. */
export const FASTHDR_DOWNLOAD_RATIO = 2.9;

/**
 * Whether a texture holds HDR data — recognised by FLOAT storage rather than by
 * name, since the source container (.exr/.hdr) is long gone by the time three
 * hands us a texture.
 */
export function isHdrTexture(texture: { type?: unknown } | null | undefined): boolean {
    const type = texture?.type;
    return type === HALF_FLOAT_TYPE || type === FLOAT_TYPE;
}

/**
 * VRAM an HDR environment could give back by shipping FastHDR instead.
 *
 * A SEPARATE estimate because the generic advice is wrong for these twice over:
 * ordinary KTX2/basis is LDR and cannot hold an HDR environment at all, and the
 * 4x colour-texture ratio understates the real win by five times. The same
 * conversion also cuts the download ~3x (FASTHDR_DOWNLOAD_RATIO) — one fix, two
 * payoffs, which is why this is worth telling apart from texture compression.
 */
export function estimateHdrEnvironmentSaving(vram: number, ratio = FASTHDR_VRAM_RATIO): number {
    if (!(vram > 0) || ratio <= 1) return 0;
    return Math.round(vram * (1 - 1 / ratio));
}

// #endregion

// #endregion



// #region Texture Format Helpers

/*
export type PixelFormat =
    | typeof AlphaFormat
    | typeof RGBFormat
    | typeof RGBAFormat
    | typeof DepthFormat
    | typeof DepthStencilFormat
    | typeof RedFormat
    | typeof RedIntegerFormat
    | typeof RGFormat
    | typeof RGIntegerFormat
    | typeof RGBIntegerFormat
    | typeof RGBAIntegerFormat;

export type DepthTexturePixelFormat = typeof DepthFormat | typeof DepthStencilFormat;

export type CompressedPixelFormat =
    | typeof RGB_S3TC_DXT1_Format
    | typeof RGBA_S3TC_DXT1_Format
    | typeof RGBA_S3TC_DXT3_Format
    | typeof RGBA_S3TC_DXT5_Format
    | typeof RGB_PVRTC_4BPPV1_Format
    | typeof RGB_PVRTC_2BPPV1_Format
    | typeof RGBA_PVRTC_4BPPV1_Format
    | typeof RGBA_PVRTC_2BPPV1_Format
    | typeof RGB_ETC1_Format
    | typeof RGB_ETC2_Format
    | typeof RGBA_ETC2_EAC_Format
    | typeof R11_EAC_Format
    | typeof SIGNED_R11_EAC_Format
    | typeof RG11_EAC_Format
    | typeof SIGNED_RG11_EAC_Format
    | typeof RGBA_ASTC_4x4_Format
    | typeof RGBA_ASTC_5x4_Format
    | typeof RGBA_ASTC_5x5_Format
    | typeof RGBA_ASTC_6x5_Format
    | typeof RGBA_ASTC_6x6_Format
    | typeof RGBA_ASTC_8x5_Format
    | typeof RGBA_ASTC_8x6_Format
    | typeof RGBA_ASTC_8x8_Format
    | typeof RGBA_ASTC_10x5_Format
    | typeof RGBA_ASTC_10x6_Format
    | typeof RGBA_ASTC_10x8_Format
    | typeof RGBA_ASTC_10x10_Format
    | typeof RGBA_ASTC_12x10_Format
    | typeof RGBA_ASTC_12x12_Format
    | typeof RGBA_BPTC_Format
    | typeof RGB_BPTC_SIGNED_Format
    | typeof RGB_BPTC_UNSIGNED_Format
    | typeof RED_RGTC1_Format
    | typeof SIGNED_RED_RGTC1_Format
    | typeof RED_GREEN_RGTC2_Format
    | typeof SIGNED_RED_GREEN_RGTC2_Format;
    */


/**
 * Reverse lookup (numeric value → export name) for three's `*Format` constants — the whole
 * AnyPixelFormat set (uncompressed, depth and compressed). Derived from three's own constants so
 * it stays complete and can never drift; this is the single source of truth shared with the
 * texture property metadata (see propertyProvider.metadata.ts).
 */
const pixelFormatNameByValue: ReadonlyMap<number, string> = new Map(
    Object.entries(THREE)
        .filter(([name, value]) => typeof value === "number" && name.endsWith("Format"))
        .map(([name, value]) => [value as number, name]),
);

export function getTexturePixelFormatAsString(format: number): string | undefined {
    return pixelFormatNameByValue.get(format);
}

/**
 * Short human label for a texture's GPU compression, or null when the texture
 * is not GPU-compressed. NOTE: this is the TRANSCODED/runtime format (what the
 * GPU actually samples) — e.g. a KTX2/Basis file arrives here as ASTC, BC7 or
 * ETC2 depending on device support. The source container (ktx2/basis) is not
 * preserved by the loaders.
 */
export function getTextureCompressionLabel(texture: { isCompressedTexture?: boolean; format?: number }): string | null {
    if (texture?.isCompressedTexture !== true || typeof texture.format !== "number") return null;
    const name = getTexturePixelFormatAsString(texture.format);
    if (!name) return `compressed (${texture.format})`;
    // three constant name → the name developers know the family by
    const astc = name.match(/ASTC_(\d+x\d+)/);
    if (astc) return `ASTC ${astc[1]}`;
    const dxt = name.match(/S3TC_DXT(\d)/);
    if (dxt) return `DXT${dxt[1]} (BC${dxt[1] === "1" ? 1 : dxt[1] === "3" ? 2 : 3})`;
    if (name.includes("RGBA_BPTC")) return "BC7";
    if (name.includes("BPTC")) return "BC6H (HDR)";
    if (name.includes("RED_GREEN_RGTC2")) return "BC5";
    if (name.includes("RGTC1")) return "BC4";
    if (name.includes("ETC2")) return "ETC2";
    if (name.includes("ETC1")) return "ETC1";
    if (name.includes("PVRTC")) return "PVRTC";
    return name.replace(/_?Format$/, "").replace(/_/g, " ");
}

/**
 * One-line format label for a texture: the GPU compression family (BC7, ASTC
 * 6x6, DXT5, ETC2, …) when compressed, else the uncompressed pixel format
 * (e.g. "RGBA · uncompressed"). For at-a-glance display on a resource node.
 */
export function textureFormatLabel(texture: { isCompressedTexture?: boolean; format?: number }): string {
    const compressed = getTextureCompressionLabel(texture);
    if (compressed) return compressed;
    const name = typeof texture?.format === "number" ? getTexturePixelFormatAsString(texture.format) : undefined;
    const family = name?.replace(/_?Format$/, "").replace(/Integer$/, "");
    return family ? `uncompressed (${family})` : "uncompressed";
}

/** "1280×1280" — a texture's pixel dimensions, or undefined if unknown. */
export function textureSizeLabel(texture: { image?: { width?: number; height?: number } }): string | undefined {
    const w = texture?.image?.width;
    const h = texture?.image?.height;
    return typeof w === "number" && typeof h === "number" && w > 0 && h > 0 ? `${w}×${h}` : undefined;
}

/**
 * "9" — how many mip levels the texture carries, or undefined when unknowable.
 *
 * Two different situations, deliberately distinguished:
 * - the texture SHIPS its chain (compressed/KTX2, DataTexture): `mipmaps.length`
 *   is the truth, and "1" means no chain — worth seeing, since a mip-less texture
 *   shimmers at distance and costs full-rate sampling.
 * - the texture has no chain of its own but `generateMipmaps` is on: the GPU will
 *   build the full chain at upload, so report the count it WILL have and mark it
 *   as generated rather than pretending we measured it.
 */
export function textureMipLabel(texture: {
    // `unknown` + narrowing: three types Texture.image/mipmaps loosely, so a
    // structural parameter type would reject a real Texture at the call site
    mipmaps?: unknown;
    generateMipmaps?: unknown;
    image?: unknown;
    isCompressedTexture?: unknown;
}): string | undefined {
    const shipped = Array.isArray(texture?.mipmaps) ? texture.mipmaps.length : 0;
    if (shipped > 0) return String(shipped);
    // A COMPRESSED texture can only ever have the levels its file ships: the GPU
    // cannot generate mips for a block format, so three ignores generateMipmaps
    // there. Without this an empty mipmaps array fell through below and reported
    // a whole synthetic chain that does not exist.
    if (texture?.isCompressedTexture === true) return "1";

    const image = texture?.image as { width?: unknown; height?: unknown } | undefined;
    const w = image?.width;
    const h = image?.height;
    if (typeof w !== "number" || typeof h !== "number" || w <= 0 || h <= 0) return undefined;
    if (!texture.generateMipmaps) return "1";
    return `${Math.floor(Math.log2(Math.max(w, h))) + 1} (generated)`;
}

/*
export type TextureDataType =
    | typeof UnsignedByteType
    | typeof ByteType
    | typeof ShortType
    | typeof UnsignedShortType
    | typeof IntType
    | typeof UnsignedIntType
    | typeof FloatType
    | typeof HalfFloatType
    | typeof UnsignedShort4444Type
    | typeof UnsignedShort5551Type
    | typeof UnsignedInt248Type
    | typeof UnsignedInt5999Type
    | typeof UnsignedInt101111Type;
    
    
export const UnsignedByteType: 1009;
export const ByteType: 1010;
export const ShortType: 1011;
export const UnsignedShortType: 1012;
export const IntType: 1013;
export const UnsignedIntType: 1014;
export const FloatType: 1015;
export const HalfFloatType: 1016;
export const UnsignedShort4444Type: 1017;
export const UnsignedShort5551Type: 1018;
export const UnsignedInt248Type: 1020;
export const UnsignedInt5999Type: 35902;
export const UnsignedInt101111Type: 35899;
    
*/

export function getTextureDataTypeAsString(type: number): string | undefined {
    switch (type) {
        case 1009: return "UnsignedByteType";
        case 1010: return "ByteType";
        case 1011: return "ShortType";
        case 1012: return "UnsignedShortType";
        case 1013: return "IntType";
        case 1014: return "UnsignedIntType";
        case 1015: return "FloatType";
        case 1016: return "HalfFloatType";
        case 1017: return "UnsignedShort4444Type";
        case 1018: return "UnsignedShort5551Type";
        case 1020: return "UnsignedInt248Type";
        case 35902: return "UnsignedInt5999Type";
        case 35899: return "UnsignedInt101111Type";
        default: return undefined;
    }
}

// #region Channel isolation (preview R/G/B/A view)

export type IsolatedTextureChannel = "r" | "g" | "b" | "a";

/**
 * Whether this texture's pixels can be reached at all: on the CPU, from a
 * drawable image source (img/bitmap/canvas/video) or a DataTexture-style typed
 * array — or on the GPU, by rendering it and reading back.
 *
 * "CPU pixels" means COLOURS you can index — not merely bytes. A compressed
 * texture does carry its mip data in JS (`mipmaps[i].data`), but block-encoded:
 * 2048x2048 in 4,194,304 bytes is one byte per pixel where RGBA needs four, so
 * nothing can ask it for the red at (x, y) without transcoding first.
 *
 * Those textures were refused for that. Render targets were then exempted
 * because the preview session gets their pixels by rendering and reading back
 * (previewProvider._snapshotChannelSource) — and a compressed texture is in the
 * same position, since the GPU decodes it while sampling. The refusal outlived
 * its reason, and it cost those textures the exposure control too, which the
 * inspector gated on this same flag.
 */
export function canIsolateTextureChannels(texture: unknown): boolean {
    if (!texture || typeof texture !== "object") return false;
    if ((texture as { isRenderTargetTexture?: boolean }).isRenderTargetTexture === true) return true;
    if (isCompressedTexture(texture as Texture)) return true;
    const image = (texture as { image?: unknown }).image;
    if (!image || typeof image !== "object") return false;
    if (isDrawableImage(image)) return true;
    const data = (image as { data?: unknown }).data;
    return ArrayBuffer.isView(data) && !(data instanceof DataView);
}

/**
 * A texture that can only be previewed by rendering a view INTO it.
 *
 * A prefiltered environment (PMREM/CubeUV) is an ATLAS — six faces and a
 * descending mip chain packed into one square with black filler — so blitting it
 * flat shows the storage format rather than the environment. Node-based
 * renderers sample one as a plain 2D map BLACK on top of that. A cube texture is
 * not a 2D image at all.
 *
 * An equirect is deliberately NOT in this set: it is one continuous image and
 * reads perfectly well as a strip.
 */
export function isEnvironmentProjection(texture: unknown): boolean {
    if (!texture || typeof texture !== "object") return false;
    const fields = texture as { isCubeTexture?: boolean, mapping?: unknown };
    return fields.isCubeTexture === true || fields.mapping === THREE.CubeUVReflectionMapping;
}

export function isDrawableImage(image: object): image is CanvasImageSource {
    // duck-typed: never instanceof host-page constructors (chrome-extension rule)
    const record = image as { tagName?: unknown; close?: unknown; getContext?: unknown; videoWidth?: unknown; width?: unknown; height?: unknown };
    if (typeof record.getContext === "function") return true; // canvas / OffscreenCanvas
    if (typeof record.videoWidth === "number") return true; // video
    if (typeof record.tagName === "string") return record.tagName === "IMG"; // HTMLImageElement
    // ImageBitmap: width/height + close(), no tag, no data array
    return typeof record.close === "function" && typeof record.width === "number" && typeof record.height === "number";
}

/**
 * Spreads ONE channel of an RGBA pixel buffer to grayscale in place
 * (r/g/b/a → rgb, alpha forced opaque). Pure — exported for tests.
 * `skipTransparent` leaves fully transparent pixels untouched — for
 * DISPLAY-space isolation, where alpha-0 pixels are the letterbox around the
 * rendered content (forcing those opaque painted the whole session canvas
 * black); it also gives the A view intuitive semantics (alpha 0 → see-through).
 */
export function swizzleChannelPixels(data: Uint8ClampedArray, channel: IsolatedTextureChannel, skipTransparent = false): void {
    const offset = channel === "r" ? 0 : channel === "g" ? 1 : channel === "b" ? 2 : 3;
    for (let i = 0; i < data.length; i += 4) {
        if (skipTransparent && data[i + 3] === 0) continue;
        const value = data[i + offset]!;
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
        data[i + 3] = 255;
    }
}

/**
 * Multiplies the color channels (never alpha) by a linear gain in place —
 * the exposure view for dim HDR buffers (bloom mips hold values ≪ 1 that
 * display near-black; note 8-bit inputs can't recover >1 highlights). Pure —
 * exported for tests. Uint8ClampedArray clamps the result.
 */
export function applyExposurePixels(data: Uint8ClampedArray, gain: number): void {
    if (gain === 1) return;
    for (let i = 0; i < data.length; i += 4) {
        data[i] = data[i]! * gain;
        data[i + 1] = data[i + 1]! * gain;
        data[i + 2] = data[i + 2]! * gain;
    }
}

/** Longest preview edge — channel review does not need full-resolution copies. */
const CHANNEL_CANVAS_MAX_SIZE = 2048;

/**
 * Builds a grayscale single-channel copy of a texture as a 2D canvas — the
 * cross-backend way to isolate channels (a raw ShaderMaterial cannot compile on
 * a WebGPU host renderer, and cross-instance TSL is off-limits). The canvas is
 * OURS (a pixel copy): using it never retains host objects. HDR/float sources
 * clamp through the 8-bit canvas — acceptable for channel inspection. Returns
 * null when the pixels are not CPU-readable.
 */
export function createChannelIsolationCanvas(texture: Texture, channel: IsolatedTextureChannel | "rgb", gain = 1): HTMLCanvasElement | null {
    try {
        const image = texture.image as unknown;
        if (!image || typeof image !== "object" || typeof document === "undefined") return null;

        let width = 0;
        let height = 0;
        let source: { data: Uint8ClampedArray; width: number; height: number } | null = null;

        const data = (image as { data?: unknown }).data;
        if (ArrayBuffer.isView(data) && !(data instanceof DataView)) {
            // DataTexture-style: normalize the typed array into RGBA bytes
            width = (image as { width?: number }).width ?? 0;
            height = (image as { height?: number }).height ?? 0;
            if (!width || !height) return null;
            const pixels = normalizeToRgbaBytes(data, width, height, texture);
            if (!pixels) return null;
            source = { data: pixels, width, height };
        }
        else if (isDrawableImage(image)) {
            width = ((image as { videoWidth?: number }).videoWidth ?? (image as { width?: number }).width ?? 0) as number;
            height = ((image as { videoHeight?: number }).videoHeight ?? (image as { height?: number }).height ?? 0) as number;
            if (!width || !height) return null;
        }
        else {
            return null;
        }

        // cap the copy — channel review does not need >2k texels
        const scale = Math.min(1, CHANNEL_CANVAS_MAX_SIZE / Math.max(width, height));
        const outWidth = Math.max(1, Math.round(width * scale));
        const outHeight = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = outWidth;
        canvas.height = outHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return null;

        if (source) {
            // draw via an intermediate so the cap also applies to data textures
            const full = document.createElement("canvas");
            full.width = width;
            full.height = height;
            const fullCtx = full.getContext("2d");
            if (!fullCtx) return null;
            // createImageData avoids the ImageData(Uint8ClampedArray<ArrayBuffer>)
            // constructor-generic strictness across TS lib versions
            const fullImage = fullCtx.createImageData(width, height);
            fullImage.data.set(source.data);
            fullCtx.putImageData(fullImage, 0, 0);
            ctx.drawImage(full, 0, 0, outWidth, outHeight);
        }
        else {
            ctx.drawImage(image as CanvasImageSource, 0, 0, outWidth, outHeight);
        }

        const imageData = ctx.getImageData(0, 0, outWidth, outHeight);
        if (channel !== "rgb") swizzleChannelPixels(imageData.data, channel);
        applyExposurePixels(imageData.data, gain);
        ctx.putImageData(imageData, 0, 0);
        return canvas;
    }
    catch {
        // tainted canvases (cross-origin without CORS), detached buffers, …
        return null;
    }
}

/**
 * Channel-isolation canvas from raw RGBA8 pixels (top-down, display-ready) —
 * the GPU-snapshot path for textures without a CPU-side image (render targets,
 * compressed, video): the preview session blits + reads the texture back once
 * and feeds the pixels here per channel.
 */
export function createChannelIsolationCanvasFromPixels(
    pixels: Uint8Array | Uint8ClampedArray,
    width: number,
    height: number,
    channel: IsolatedTextureChannel | "rgb",
    gain = 1,
    /** Force alpha opaque (render-target textures: content lives in RGB with
     *  alpha 0). TEXEL-space — only the texture's own pixels, so a letterboxed
     *  view keeps its transparent surround. */
    opaque = false,
): HTMLCanvasElement | null {
    try {
        if (typeof document === "undefined" || !width || !height || pixels.length !== width * height * 4) return null;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(pixels);
        if (channel !== "rgb") swizzleChannelPixels(imageData.data, channel);
        applyExposurePixels(imageData.data, gain);
        if (opaque) {
            const data = imageData.data;
            for (let i = 3; i < data.length; i += 4) data[i] = 255;
        }
        ctx.putImageData(imageData, 0, 0);
        return canvas;
    }
    catch {
        return null;
    }
}

/** RGBA bytes from a DataTexture array (uint8 passthrough; float/half clamp 0..1). */
function normalizeToRgbaBytes(data: ArrayBufferView, width: number, height: number, texture: Texture): Uint8ClampedArray | null {
    const pixelCount = width * height;
    const array = data as unknown as { length: number;[index: number]: number };
    const channels = Math.round(array.length / pixelCount);
    if (channels < 1 || channels > 4 || channels * pixelCount !== array.length) return null;
    const isFloat = texture.type === FloatType || texture.type === HalfFloatType;
    const out = new Uint8ClampedArray(pixelCount * 4);
    for (let p = 0; p < pixelCount; p++) {
        const src = p * channels;
        const dst = p * 4;
        for (let c = 0; c < 4; c++) {
            if (c < channels) {
                const raw = array[src + c]!;
                out[dst + c] = isFloat ? Math.round(Math.min(1, Math.max(0, raw)) * 255) : raw;
            }
            else {
                out[dst + c] = c === 3 ? 255 : (channels === 1 ? out[dst]! : 0);
            }
        }
    }
    return out;
}

// #endregion

/** How many mip levels a texture can actually be shown at (1 = just the base). */
export function textureMipLevelCount(texture: {
    mipmaps?: unknown;
    generateMipmaps?: unknown;
    image?: unknown;
    isCompressedTexture?: unknown;
}): number {
    const shipped = Array.isArray(texture?.mipmaps) ? texture.mipmaps.length : 0;
    if (shipped > 0) return shipped;
    // compressed: only what the file ships (see textureMipLabel) — an empty
    // mipmaps array here means ONE level, not a generated chain
    if (texture?.isCompressedTexture === true) return 1;
    const image = texture?.image as { width?: unknown; height?: unknown } | undefined;
    const w = image?.width;
    const h = image?.height;
    if (!texture?.generateMipmaps || typeof w !== "number" || typeof h !== "number" || w <= 0 || h <= 0) return 1;
    return Math.floor(Math.log2(Math.max(w, h))) + 1;
}

/**
 * Whether this renderer's `copyTextureToTexture` accepts the srcLevel/dstLevel
 * arguments (three r167+). Detected from the function's PARAMETER COUNT, which
 * minification preserves — older builds take four (src, dst, srcRegion,
 * dstPosition) and would silently copy mip 0 while we labelled it level N.
 */
export function supportsMipLevelCopy(renderer: { copyTextureToTexture?: unknown }): boolean {
    const fn = renderer?.copyTextureToTexture;
    if (typeof fn !== "function") return false;
    try {
        const signature = Function.prototype.toString.call(fn);
        const params = signature.slice(signature.indexOf("(") + 1, signature.indexOf(")"));
        if (!params.trim()) return false;
        return params.split(",").length >= 5;
    }
    catch {
        return false; // exotic/native function — assume the older contract
    }
}
